/**
 * 媒体模块组件
 * 功能：
 * 1. 视频模块 + 图片/租赁模块混合展示
 * 2. 视频在进入可视窗口时自动播放（移动端滚动触发，桌面端顺序播放）
 * 3. 点击视频可暂停/播放
 * 4. 显示视频播放进度环
 * 5. 导航点切换不同模块
 */
class MediaModulesComponent extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.autoPlay = this.dataset.autoPlay === 'true';
    this.showProgress = this.dataset.showProgress === 'true';
    this.mobileScrollPlay = this.dataset.mobileScrollPlay === 'true';

    this.videos = [];
    this.currentVideoIndex = 0;
    this.isAutoPlaying = false;
    this.isMobile = window.innerWidth <= 990;
    this.mobileObserver = null;
    this.autoPlayTimeout = null;
    this.isPaused = false;

    this.carouselRows = [];
    this.lastScrollY = window.scrollY;
    this.scrollDirectionDown = true;

    this.init();
  }

  init() {
    this.setupVideos();
    this.setupCarouselTriggers();
    this.bindCarouselPlayButtons();
    this.setupScrollDirectionTracker();
    this.setupComponentObserver();
    this.setupNavigation();
    this.setupIntersectionObserver();
    this.bindEvents();
    this.handleInitialAutoPlay();
  }

  /**
   * 初始化进度环
   */
  initProgressCircle(circle, radius = 45) {
    if (!circle || !this.showProgress) return null;
    const circumference = 2 * Math.PI * radius;
    
    // 设置 stroke-dasharray（如果未设置）
    if (!circle.getAttribute('stroke-dasharray')) {
      circle.style.strokeDasharray = circumference;
    }
    
    // 设置初始 stroke-dashoffset（从顶部12点钟位置开始，完全隐藏）
    const currentOffset = circle.style.strokeDashoffset || circle.getAttribute('stroke-dashoffset');
    if (!currentOffset || currentOffset === 'none') {
      circle.style.strokeDashoffset = circumference;
    }
    
    return circumference;
  }

  /**
   * 创建视频数据对象
   */
  createVideoData(video, options = {}) {
    const {
      module = video.closest('.media-modules__video-module'),
      playPauseBtn = module?.querySelector('.media-modules__play-pause-btn, [data-carousel-play-btn]'),
      progressCircle = module?.querySelector('[data-progress-circle], [data-carousel-progress]'),
      index = this.videos.length,
      type = 'default',
      radius = 45
    } = options;

    // 检查是否是 poster-only 图片
    const isPosterOnly = video.dataset.posterOnly === '' || video.hasAttribute('data-poster-only');
    const fixedDuration = isPosterOnly ? 5 : 0; // 固定 5 秒

    // 移动端视频不需要进度环
    const circumference = type === 'mobile' ? null : this.initProgressCircle(progressCircle, radius);

    return {
      element: video,
      module,
      playPauseBtn,
      progressCircle: type === 'mobile' ? null : progressCircle,
      index,
      type,
      isPlaying: false,
      duration: fixedDuration,
      circumference,
      progressRAF: null,
      isPosterOnly,
      posterTimer: null,
      posterStartTime: null
    };
  }

  /**
   * 绑定视频事件
   */
  bindVideoEvents(videoData, callbacks = {}) {
    // Poster 图片不需要视频事件
    if (videoData.isPosterOnly) return;
    
    const video = videoData.element;
    const {
      onLoadedMetadata = () => { videoData.duration = video.duration; },
      onEnded = () => { this.onVideoEnded(videoData); },
      onTimeUpdate = videoData.type === 'mobile' ? () => {} : () => { this.updateProgress(videoData); },
      onPlay = () => {
        videoData.isPlaying = true;
        videoData.playPauseBtn?.classList.add('playing');
      },
      onPause = () => {
        videoData.isPlaying = false;
        videoData.playPauseBtn?.classList.remove('playing');
      }
    } = callbacks;

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('ended', onEnded);
    video.addEventListener('timeupdate', onTimeUpdate, { passive: true });
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);

    if (videoData.playPauseBtn) {
      videoData.playPauseBtn.addEventListener('click', () => {
        this.toggleVideo(videoData);
      });
    }
  }

  /**
   * 设置所有视频元素
   */
  setupVideos() {
    const processedVideos = new Set();

    // 先处理 Carousel 视频（桌面端）
    this.querySelectorAll('[data-carousel-video]').forEach((video) => {
      processedVideos.add(video);
      const module = video.closest('.media-modules__carousel-module');
      const playBtn = module?.querySelector('[data-carousel-play-btn]');
      const progressCircle = module?.querySelector('[data-carousel-progress]');
      const isPosterOnly = video.dataset.posterOnly === '' || video.hasAttribute('data-poster-only');
      const videoData = this.createVideoData(video, {
        module,
        playPauseBtn: playBtn,
        progressCircle,
        type: 'carousel',
        radius: 45
      });

      this.videos.push(videoData);
      
      if (isPosterOnly) {
        // Poster 图片不需要视频事件，但需要绑定点击事件
        if (playBtn) {
          playBtn.addEventListener('click', () => {
            this.toggleVideo(videoData);
          });
        }
      } else {
        this.bindVideoEvents(videoData, {
          onEnded: () => {
            playBtn?.classList.remove('playing');
            videoData.isPlaying = false;
            this.onCarouselVideoEnded(videoData);
          }
        });
      }
    });

    // 处理 Mobile carousel 视频
    this.querySelectorAll('[data-carousel-mobile-video]').forEach((video) => {
      processedVideos.add(video);
      const module = video.closest('.media-modules__carousel-module');
      const playBtn = module?.querySelector('[data-carousel-mobile-play-btn]');
      const videoData = this.createVideoData(video, {
        module,
        playPauseBtn: playBtn,
        progressCircle: null,
        type: 'carousel-mobile'
      });

      this.videos.push(videoData);
      this.bindVideoEvents(videoData, {
        onEnded: () => {
          playBtn?.classList.remove('playing');
          videoData.isPlaying = false;
        }
      });
    });

    // 处理移动端视频（.media-modules__mobile-video）
    this.querySelectorAll('.media-modules__mobile-video').forEach((video) => {
      if (processedVideos.has(video)) return;
      processedVideos.add(video);
      const module = video.closest('.mobile-video-container');
      // 使用视频元素上的 data-module-index，如果没有则使用容器上的
      const moduleIndex = parseInt(video.dataset.moduleIndex) || parseInt(module?.dataset.moduleIndex) || this.videos.length;
      const isPosterOnly = video.dataset.posterOnly === '' || video.hasAttribute('data-poster-only');
      const videoData = this.createVideoData(video, {
        module,
        playPauseBtn: null,
        progressCircle: null,
        type: 'mobile',
        index: moduleIndex,
        radius: 0 // 移动端不需要进度环
      });

      this.videos.push(videoData);
      
      if (!isPosterOnly) {
        // 移动端视频只需要基本的事件绑定（非 poster 图片）
        this.bindVideoEvents(videoData);
      }
    });

    // 最后处理普通视频模块（排除已处理的 carousel 视频）
    this.querySelectorAll('.media-modules__video').forEach((video) => {
      if (processedVideos.has(video)) return;

      const module = video.closest('.media-modules__video-module');
      const progressCircle = module?.querySelector('[data-progress-circle]');
      const videoData = this.createVideoData(video, {
        module,
        progressCircle,
        radius: 45
      });

      if (module?.dataset.autoPoster === 'true') {
        videoData.autoPoster = true;
        video.addEventListener('loadeddata', () => {
          if (!video.getAttribute('poster')) {
            this.generatePosterFromFirstFrame(video);
          }
        });
      }

      this.videos.push(videoData);
      this.bindVideoEvents(videoData);
    });
  }

  /**
   * 设置 carousel 触发器
   */
  setupCarouselTriggers() {
    this.querySelectorAll('.media-modules__module-row--carousel').forEach((row) => {
      const items = Array.from(row.querySelectorAll('[data-row-trigger]'));
      const videos = Array.from(row.querySelectorAll('[data-carousel-video]'));
      const playBtn = row.querySelector('[data-carousel-play-btn]');

      const rowObj = {
        row,
        items,
        videos,
        videoEl: videos[0] || null,
        playBtn,
        currentIndex: 0,
        autoPlay: false
      };
      this.carouselRows.push(rowObj);

      // 初始化状态
      items.forEach((it, idx) => it.classList.toggle('is-active', idx === 0));
      videos.forEach((v, idx) => v.classList.toggle('is-active', idx === 0));

      // 关联进度环
      items.forEach((item, idx) => {
        const videoData = videos.length > 1
          ? this.videos.find(d => d.element === videos[idx])
          : this.videos.find(d => d.element === rowObj.videoEl);

        const itemProgress = item.querySelector('[data-item-progress]');
        if (videoData && itemProgress) {
          videoData.circumference = this.initProgressCircle(itemProgress, 48);
          videoData.progressCircle = itemProgress;
        }

        item.addEventListener('click', () => {
          this.activateRowIndex(rowObj, idx, true);
        });

        const itemBtn = item.querySelector('[data-item-play-btn]');
        if (itemBtn) {
          itemBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (rowObj.currentIndex !== idx) {
              this.activateRowIndex(rowObj, idx, true);
            } else {
              const activeVideoEl = videos.length > 1 ? videos[idx] : rowObj.videoEl;
              const data = this.videos.find(d => d.element === activeVideoEl);
              if (data) {
                this.toggleVideo(data);
                // 对于 poster 图片，检查 isPlaying 状态；对于视频，检查 paused 状态
                const isPlaying = data.isPosterOnly ? data.isPlaying : !data.element.paused;
                item.classList.toggle('is-playing', isPlaying);
              }
            }
          });
        }
      });
    });
  }

  /**
   * 绑定 carousel 播放按钮
   */
  bindCarouselPlayButtons() {
    this.querySelectorAll('.media-modules__carousel-module').forEach((module) => {
      const playBtn = module.querySelector('[data-carousel-play-btn]');
      if (!playBtn || playBtn.dataset.bound === 'true') return;
      playBtn.dataset.bound = 'true';

      playBtn.addEventListener('click', (event) => {
        event.stopImmediatePropagation();
        const activeVideoEl = module.querySelector('[data-carousel-video].is-active');
        const data = this.videos.find(d => d.element === activeVideoEl);
        if (data) this.toggleVideo(data);
      }, { capture: true });
    });
  }

  /**
   * 跟踪滚动方向
   */
  setupScrollDirectionTracker() {
    window.addEventListener('scroll', this.throttle(() => {
      const y = window.scrollY;
      this.scrollDirectionDown = y >= this.lastScrollY;
      this.lastScrollY = y;
    }, 100));
  }

  /**
   * 观察 carousel 行
   */
  setupComponentObserver() {
    const rows = this.querySelectorAll('.media-modules__module-row--carousel');
    if (!rows.length) return;

    const rowObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rowObj = this.carouselRows.find(r => r.row === entry.target);
        if (!rowObj) return;

        if (entry.isIntersecting) {
          const startIndex = this.scrollDirectionDown ? 0 : (rowObj.videos.length - 1);
          rowObj.currentIndex = Math.max(0, startIndex);
          this.activateRowIndex(rowObj, rowObj.currentIndex, true);
          rowObj.autoPlay = true;
        } else {
          rowObj.autoPlay = false;
          rowObj.videos.forEach((v) => {
            const data = this.videos.find(d => d.element === v);
            if (data) this.pauseVideo(data);
          });
        }
      });
    }, { threshold: 0.35 });

    rows.forEach(row => rowObserver.observe(row));
  }

  /**
   * 设置导航点
   */
  setupNavigation() {
    this.querySelectorAll('.media-modules__nav-dot').forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToModule(index));
    });
    this.updateNavigation();
  }

  /**
   * 设置交叉观察器（移动端）
   */
  setupIntersectionObserver() {
    if (!this.mobileScrollPlay || !this.isMobile) return;

    this.mobileObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // 查找对应的视频数据
        let videoData = null;
        
        // 优先通过 moduleIndex 匹配
        const moduleIndex = parseInt(entry.target.dataset.moduleIndex);
        if (!isNaN(moduleIndex)) {
          videoData = this.videos.find(v => v.type === 'mobile' && v.index === moduleIndex);
        }
        
        // 如果没找到，通过视频元素直接匹配
        if (!videoData) {
          const videoElement = entry.target.classList?.contains('media-modules__mobile-video')
            ? entry.target
            : entry.target.querySelector?.('.media-modules__mobile-video');
          if (videoElement) {
            videoData = this.videos.find(v => v.type === 'mobile' && v.element === videoElement);
          }
        }
        
        if (!videoData) return;

        // 进入可视区域时播放，离开时暂停
        if (entry.isIntersecting) {
          this.playVideo(videoData);
        } else {
          this.pauseVideo(videoData);
        }
      });
    }, { threshold: 0.5 });

    // 只观察移动端视频容器
    this.videos.forEach(videoData => {
      if (videoData.type === 'mobile' && videoData.module) {
        this.mobileObserver.observe(videoData.module);
      }
    });
  }

  /**
   * 绑定全局事件
   */
  bindEvents() {
    window.addEventListener('resize', this.throttle(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 990;
      if (wasMobile !== this.isMobile) {
        this.handleModeChange();
      }
    }, 250));

    this.videos.forEach(videoData => {
      videoData.playPauseBtn?.addEventListener('click', () => {
        this.pauseAutoPlay();
      });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllVideos();
      } else if (this.autoPlay && !this.isMobile && !this.isPaused) {
        this.resumeAutoPlay();
      }
    });
  }

  /**
   * 处理初始自动播放
   */
  handleInitialAutoPlay() {
    if (!this.autoPlay || this.isMobile || !this.videos.length) return;
    const hasCarousel = !!this.querySelector('.media-modules__module-row--carousel');
    if (!hasCarousel) {
      this.startAutoPlaySequence();
    }
  }

  /**
   * 处理模式切换
   */
  handleModeChange() {
    if (this.isMobile) {
      this.stopAutoPlay();
      // 重新设置移动端观察器
      if (this.mobileObserver) {
        this.mobileObserver.disconnect();
        this.mobileObserver = null;
      }
      this.setupIntersectionObserver();
    } else {
      // 桌面端：清理移动端观察器
      if (this.mobileObserver) {
        this.mobileObserver.disconnect();
        this.mobileObserver = null;
      }
      // 暂停所有移动端视频
      this.videos.forEach(videoData => {
        if (videoData.type === 'mobile' && videoData.isPlaying) {
          this.pauseVideo(videoData);
        }
      });
      if (this.autoPlay && !this.isPaused) {
        const hasCarousel = !!this.querySelector('.media-modules__module-row--carousel');
        if (!hasCarousel) {
          this.startAutoPlaySequence();
        }
      }
    }
  }

  /**
   * 开始自动播放序列
   */
  startAutoPlaySequence() {
    if (this.isMobile || !this.autoPlay) return;
    this.stopAutoPlay();
    this.currentVideoIndex = 0;
    this.isAutoPlaying = true;
    this.playCurrentVideo();
  }

  /**
   * 播放当前视频
   */
  playCurrentVideo() {
    if (!this.isAutoPlaying || this.isMobile) return;
    const videoData = this.videos[this.currentVideoIndex];
    if (videoData) {
      this.playVideo(videoData);
      this.updateNavigation();
    }
  }

  /**
   * 视频播放结束回调
   */
  onVideoEnded(videoData) {
    if (this.isAutoPlaying && !this.isMobile) {
      this.currentVideoIndex = (this.currentVideoIndex + 1) % this.videos.length;
      this.autoPlayTimeout = setTimeout(() => {
        this.playCurrentVideo();
      }, 500);
    }
  }

  /**
   * Carousel 视频结束回调
   */
  onCarouselVideoEnded(videoData) {
    const rowObj = this.carouselRows.find(r => r.videos.includes(videoData.element));
    if (rowObj?.autoPlay) {
      const nextIndex = (rowObj.currentIndex + 1) % rowObj.videos.length;
      this.activateRowIndex(rowObj, nextIndex, true);
    }
  }

  /**
   * 播放视频
   */
  playVideo(videoData) {
    if (!videoData?.element) return;

    this.videos.forEach(v => {
      if (v !== videoData && v.isPlaying) {
        this.pauseVideo(v);
      }
    });

    // 如果是 poster 图片，启动 5 秒定时器
    if (videoData.isPosterOnly) {
      videoData.isPlaying = true;
      videoData.playPauseBtn?.classList.add('playing');
      videoData.posterStartTime = Date.now();
      
      // 清除之前的定时器
      if (videoData.posterTimer) {
        clearTimeout(videoData.posterTimer);
      }
      
      // 启动进度更新
      this.startPosterProgress(videoData);
      
      // 5 秒后触发结束事件
      videoData.posterTimer = setTimeout(() => {
        videoData.isPlaying = false;
        videoData.playPauseBtn?.classList.remove('playing');
        this.onPosterEnded(videoData);
      }, 5000);
    } else {
      // 普通视频播放
      const playPromise = videoData.element.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Video play failed:', error);
        });
      }
    }
  }

  /**
   * 暂停视频
   */
  pauseVideo(videoData) {
    if (!videoData?.element) return;
    
    // 如果是 poster 图片，清除定时器
    if (videoData.isPosterOnly) {
      if (videoData.posterTimer) {
        clearTimeout(videoData.posterTimer);
        videoData.posterTimer = null;
      }
      videoData.isPlaying = false;
      videoData.playPauseBtn?.classList.remove('playing');
      videoData.posterStartTime = null;
      if (videoData.progressRAF) {
        cancelAnimationFrame(videoData.progressRAF);
        videoData.progressRAF = null;
      }
    } else {
      videoData.element.pause();
      if (videoData.progressRAF) {
        cancelAnimationFrame(videoData.progressRAF);
        videoData.progressRAF = null;
      }
    }
  }

  /**
   * 切换视频播放/暂停
   */
  toggleVideo(videoData) {
    if (videoData.isPlaying) {
      this.pauseVideo(videoData);
      this.pauseAutoPlay();
    } else {
      this.playVideo(videoData);
      if (this.isAutoPlaying) {
        this.currentVideoIndex = videoData.index;
      }
    }
  }

  /**
   * 激活 carousel 行索引
   */
  activateRowIndex(rowObj, targetIndex, shouldPlay = false) {
    const { items, videos, playBtn } = rowObj;
    rowObj.currentIndex = targetIndex;

    items.forEach((it, idx) => it.classList.toggle('is-active', idx === targetIndex));
    if (videos.length > 1) {
      videos.forEach((v, idx) => v.classList.toggle('is-active', idx === targetIndex));
    } else if (rowObj.videoEl) {
      rowObj.videoEl.classList.add('is-active');
    }

    const activeVideoEl = videos.length > 1 ? videos[targetIndex] : rowObj.videoEl;
    const data = this.videos.find(d => d.element === activeVideoEl);
    if (!data) return;

    // 更新进度环引用
    const activeItem = items[targetIndex];
    if (activeItem) {
      const itemProgress = activeItem.querySelector('[data-item-progress]');
      if (itemProgress) {
        data.progressCircle = itemProgress;
        if (this.showProgress && !data.circumference) {
          data.circumference = 2 * Math.PI * 48;
        }
      }
    }

    this.videos.forEach(vd => {
      if (vd !== data) this.pauseVideo(vd);
    });

    if (shouldPlay) this.playVideo(data);
    
    // 更新播放按钮状态（支持 poster 图片和视频）
    const isPlaying = data.isPosterOnly ? data.isPlaying : (!!data.element && !data.element.paused);
    playBtn?.classList.toggle('playing', isPlaying);
    items.forEach((it, idx) => {
      const btn = it.querySelector('[data-item-play-btn]');
      if (btn) it.classList.toggle('is-playing', idx === targetIndex && isPlaying);
    });
  }

  /**
   * 启动 poster 图片的进度更新
   */
  startPosterProgress(videoData) {
    if (!videoData.isPosterOnly || !this.showProgress || !videoData.progressCircle) return;
    
    const updateProgress = () => {
      if (!videoData.isPlaying || !videoData.posterStartTime) return;
      
      const elapsed = (Date.now() - videoData.posterStartTime) / 1000; // 秒
      const progress = Math.min(elapsed / videoData.duration, 1);
      const circumference = videoData.circumference || (2 * Math.PI * 45);
      const offset = circumference - (progress * circumference);
      
      if (videoData.progressCircle) {
        videoData.progressCircle.style.strokeDashoffset = offset;
      }
      
      if (videoData.isPlaying && progress < 1) {
        videoData.progressRAF = requestAnimationFrame(updateProgress);
      }
    };
    
    updateProgress();
  }

  /**
   * 更新进度环（统一方法）
   */
  updateProgress(videoData) {
    // Poster 图片使用专门的进度更新方法
    if (videoData.isPosterOnly) {
      this.startPosterProgress(videoData);
      return;
    }
    
    // 移动端视频不需要进度环
    if (videoData.type === 'mobile') return;
    if (!this.showProgress || !videoData.progressCircle || !videoData.duration) return;

    if (videoData.progressRAF) {
      cancelAnimationFrame(videoData.progressRAF);
    }

    videoData.progressRAF = requestAnimationFrame(() => {
      const progress = videoData.element.currentTime / videoData.duration;
      const circumference = videoData.circumference || (2 * Math.PI * 45);
      const offset = circumference - (progress * circumference);
      videoData.progressCircle.style.strokeDashoffset = offset;
      videoData.progressRAF = null;
    });
  }
  
  /**
   * Poster 图片结束回调
   */
  onPosterEnded(videoData) {
    // 重置进度环
    if (videoData.progressCircle && videoData.circumference) {
      videoData.progressCircle.style.strokeDashoffset = videoData.circumference;
    }
    
    // 根据类型调用相应的结束回调
    if (videoData.type === 'carousel') {
      this.onCarouselVideoEnded(videoData);
    } else {
      this.onVideoEnded(videoData);
    }
  }

  /**
   * 暂停自动播放
   */
  pauseAutoPlay() {
    this.isPaused = true;
    this.stopAutoPlay();
  }

  /**
   * 恢复自动播放
   */
  resumeAutoPlay() {
    if (!this.autoPlay || this.isMobile) return;
    this.isPaused = false;
    this.startAutoPlaySequence();
  }

  /**
   * 停止自动播放
   */
  stopAutoPlay() {
    this.isAutoPlaying = false;
    if (this.autoPlayTimeout) {
      clearTimeout(this.autoPlayTimeout);
      this.autoPlayTimeout = null;
    }
  }

  /**
   * 暂停所有视频
   */
  pauseAllVideos() {
    this.videos.forEach(videoData => {
      if (videoData.isPlaying) {
        this.pauseVideo(videoData);
      }
    });
  }

  /**
   * 跳转到指定模块
   */
  goToModule(index) {
    if (index < 0 || index >= this.videos.length) return;
    this.currentVideoIndex = index;

    if (this.isAutoPlaying && !this.isMobile) {
      this.playCurrentVideo();
    }

    this.updateNavigation();

    const targetModule = this.videos[index]?.module;
    if (targetModule) {
      targetModule.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  /**
   * 更新导航点状态
   */
  updateNavigation() {
    this.querySelectorAll('.media-modules__nav-dot').forEach((dot, index) => {
      dot.classList.toggle('active', index === this.currentVideoIndex);
    });
  }

  /**
   * 生成视频 poster
   */
  generatePosterFromFirstFrame(videoEl) {
    try {
      const width = videoEl.videoWidth || 0;
      const height = videoEl.videoHeight || 0;
      if (!width || !height) return;

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(videoEl, 0, 0, width, height);
      const dataURL = canvas.toDataURL('image/jpeg', 0.85);
      videoEl.setAttribute('poster', dataURL);
      if (!videoEl.paused) videoEl.pause();
    } catch (e) {
      console.warn('Auto poster generation failed (likely CORS). Provide a poster image.', e);
    }
  }

  /**
   * 节流函数
   */
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }

  // 公共方法
  play(index = null) {
    const targetIndex = index !== null ? index : this.currentVideoIndex;
    const videoData = this.videos[targetIndex];
    if (videoData) this.playVideo(videoData);
  }

  pause(index = null) {
    const targetIndex = index !== null ? index : this.currentVideoIndex;
    const videoData = this.videos[targetIndex];
    if (videoData) this.pauseVideo(videoData);
  }

  next() {
    const nextIndex = (this.currentVideoIndex + 1) % this.videos.length;
    this.goToModule(nextIndex);
  }

  previous() {
    const prevIndex = this.currentVideoIndex === 0
      ? this.videos.length - 1
      : this.currentVideoIndex - 1;
    this.goToModule(prevIndex);
  }

  /**
   * 组件卸载清理
   */
  disconnectedCallback() {
    this.stopAutoPlay();

    if (this.mobileObserver) {
      this.mobileObserver.disconnect();
    }

    this.videos.forEach(videoData => {
      if (videoData.progressRAF) {
        cancelAnimationFrame(videoData.progressRAF);
        videoData.progressRAF = null;
      }
      // 清理 poster 定时器
      if (videoData.posterTimer) {
        clearTimeout(videoData.posterTimer);
        videoData.posterTimer = null;
      }
    });
  }
}

// 注册自定义元素
customElements.define('media-modules-component', MediaModulesComponent);

/**
 * Shopify 主题编辑器事件处理
 */
if (window.Shopify && window.Shopify.designMode) {
  const handleSectionEvent = (event) => {
    const mediaModules = event.target.querySelector('media-modules-component');
    if (mediaModules) {
      setTimeout(() => mediaModules.init(), 100);
    }
  };

  document.addEventListener('shopify:section:load', handleSectionEvent);
  document.addEventListener('shopify:section:reorder', handleSectionEvent);

  document.addEventListener('shopify:block:select', (event) => {
    const mediaModules = event.target.closest('media-modules-component');
    const block = event.target;
    if (mediaModules && block) {
      const moduleIndex = parseInt(block.dataset.moduleIndex);
      if (!isNaN(moduleIndex)) {
        mediaModules.goToModule(moduleIndex);
      }
    }
  });
}

/**
 * 处理浏览器前进/后退导航
 */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    document.querySelectorAll('media-modules-component').forEach(component => {
      component.handleInitialAutoPlay();
    });
  }
});

/**
 * ESC 键暂停所有视频
 */
document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    document.querySelectorAll('media-modules-component').forEach(component => {
      component.pauseAllVideos();
      component.pauseAutoPlay();
    });
  }
});
